import React, { useState } from "react";
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, TextField, IconButton, Typography, CircularProgress,
    Snackbar, Menu, MenuItem,
    FormControl, InputLabel, Select, Box, Checkbox, TablePagination
} from "@mui/material";
import { Card, CardContent } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import PaymentIcon from "@mui/icons-material/Payment";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import { GET_MEMBERS } from "../graphql/queries";
import EditUserDialog from "./Edituserdialog";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import PaymentConfirmationDialog from "./PaymentConfirmationDialog";
import GroupsIcon from '@mui/icons-material/Groups';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import MoneyOffIcon from '@mui/icons-material/MoneyOff';
import NotLoggedIn from "../components/LoginExceptions";

const DataTable = () => {
    const { loading, error, data, refetch } = useQuery(GET_MEMBERS);
    const [filters, setFilters] = useState({
        First_Name: "",
        Mobile_Number: "",
        id: "",
        Payment_Status: "",
        ID_Card: "",
    });
    const [openEditModal, setOpenEditModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [selectedUserForDelete, setSelectedUserForDelete] = useState(null);
    const [selectedUserForPayment, setSelectedUserForPayment] = useState(null);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const [menuUser, setMenuUser] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [dialogMode, setDialogMode] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const navigate = useNavigate();
    const storedUserType = localStorage.getItem("user_type");

    if (!storedUserType) {
        return <NotLoggedIn />;
    }

    const handleRefresh = () => {
        setFilters({
            First_Name: "",
            Mobile_Number: "",
            id: "",
            Payment_Status: "",
            ID_Card: "",
        });
        refetch();
    };

    const handleCreate = () => navigate("/userprofile"); window.scrollTo(0, 0);

    const handleEdit = (user) => {
        handleMenuClose();
        setSelectedUser(user);
        setOpenEditModal(true);
    };

    const handlePayment = (user) => {
        handleMenuClose();
        setSelectedUserForPayment(user);
        setDialogMode("pay");
        setOpenDialog(true);
    };

    const handleDelete = (user) => {
        handleMenuClose();
        setSelectedUserForDelete(user);
        setDialogMode("delete");
        setOpenDialog(true);
    };

    const handleMenuOpen = (event, user) => {
        setMenuAnchorEl(event.currentTarget);
        setMenuUser(user);
    };

    const handleMenuClose = () => {
        setMenuAnchorEl(null);
        setMenuUser(null);
    };

    const handleCloseSnackbar = () => setSnackbarOpen(false);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id) => selectedUsers.includes(id);

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const allIds = filteredData.map((user) => user.id);
            setSelectedUsers(allIds);
        } else {
            setSelectedUsers([]);
        }
    };

    const handleCheckboxClick = (event, id) => {
        event.stopPropagation();
        setSelectedUsers((prev) =>
            prev.includes(id) ? prev.filter((uid) => uid !== id) : [...prev, id]
        );
    };

    const filteredData = data?.members?.filter((row) => {
        return Object.keys(filters).every((key) => {
            if (!filters[key]) return true;
            const rowValue = row[key]?.toString().toLowerCase();
            const filterValue = filters[key].toLowerCase();
            if (key === "Payment_Status") {
                return row.Payment_Status?.toLowerCase() === filterValue;
            }
            if (key === "ID_Card") {
                if (filterValue === "generated") {
                    return rowValue === "generated";
                }
                if (filterValue === "not generated") {
                    return rowValue !== "generated";
                }
                return true;
            }
            return rowValue?.includes(filterValue);
        });
    }) || [];

    const StatCard = ({ title, count, icon, iconColor }) => (
        <Card
            sx={{
                minWidth: 160,
                backgroundColor: "#fff",
                borderRadius: 2,
                border: "2px solid #57511F",
                boxShadow: "none",
            }}
        >
            <CardContent sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: '16px !important' }}>
                <Box>
                    <Typography variant="body2" color="text.secondary">
                        {title}
                    </Typography>
                    <Typography variant="h6" fontWeight="bold">
                        {count}
                    </Typography>
                </Box>
                <Box sx={{ color: iconColor }}>
                    {icon}
                </Box>
            </CardContent>
        </Card>
    );

    return (
        <div style={{ padding: 20 }}>
            {/* Title and Actions */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <Typography variant="h4" sx={{ fontWeight: "bold", color: "#2A289C" }}>
                    Manage Users
                </Typography>
                <div style={{ display: "flex", gap: 10 }}>
                    <Button variant="contained" color="success" onClick={handleCreate}>
                        Create
                    </Button>
                    <IconButton color="primary" onClick={handleRefresh}>
                        <RefreshIcon />
                    </IconButton>
                </div>
            </div>

            {/* Stat Cards */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" }, // Column on mobile, row on tablet and up
                    gap: 2,
                    mb: 2,
                    flexWrap: "wrap", // Allow wrap on smaller screens
                }}
            >
                <StatCard
                    title="Total"
                    count={filteredData.filter(user => ["Paid", "Unpaid", "Failed"].includes(user.Payment_Status)).length}
                    icon={<GroupsIcon sx={{ fontSize: 50 }} />}
                    iconColor="blue"
                />
                <StatCard
                    title="Paid"
                    count={filteredData.filter(user => user.Payment_Status === "Paid").length}
                    icon={<AttachMoneyIcon sx={{ fontSize: 50 }} />}
                    iconColor="green"
                />
                <StatCard
                    title="Unpaid"
                    count={filteredData.filter(user => ["Unpaid", "Failed"].includes(user.Payment_Status)).length}
                    icon={<MoneyOffIcon sx={{ fontSize: 50 }} />}
                    iconColor="red"
                />
            </Box>
            {/* Pagination Info */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <Typography variant="body2" sx={{ marginRight: 2 }}>
                    Total Pages: {Math.ceil(filteredData.length / rowsPerPage)}
                </Typography>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredData.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </div>

            {/* Filter Fields */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                <TextField label="Name" size="small" variant="outlined"
                    value={filters.First_Name} onChange={(e) => setFilters({ ...filters, First_Name: e.target.value })} />
                <TextField label="Mobile Number" size="small" variant="outlined"
                    value={filters.Mobile_Number} onChange={(e) => setFilters({ ...filters, Mobile_Number: e.target.value })} />
                <TextField label="Member ID" size="small" variant="outlined"
                    value={filters.id} onChange={(e) => setFilters({ ...filters, id: e.target.value })} />
                <FormControl size="small" sx={{ minWidth: 170 }}>
                    <InputLabel>Payment Status</InputLabel>
                    <Select value={filters.Payment_Status} onChange={(e) => setFilters({ ...filters, Payment_Status: e.target.value })} label="Payment Status">
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Unpaid">Unpaid</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                    </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>ID Card Status</InputLabel>
                    <Select value={filters.ID_Card} onChange={(e) => setFilters({ ...filters, ID_Card: e.target.value })} label="ID Card Status">
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="Generated">Generated</MenuItem>
                        <MenuItem value="Not Generated">Not Generated</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Main Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead sx={{ backgroundColor: "#FAD707" }}>
                        <TableRow>
                            <TableCell padding="checkbox">
                                <Checkbox
                                    sx={{
                                        color: "#57511F",
                                        '&.Mui-checked': { color: "#57511F" },
                                    }}
                                    indeterminate={selectedUsers.length > 0 && selectedUsers.length < filteredData.length}
                                    checked={filteredData.length > 0 && selectedUsers.length === filteredData.length}
                                    onChange={handleSelectAllClick}
                                />
                            </TableCell>
                            <TableCell><b>Name</b></TableCell>
                            <TableCell><b>Mobile Number</b></TableCell>
                            <TableCell><b>Member ID</b></TableCell>
                            <TableCell><b>Payment</b></TableCell>
                            <TableCell><b>ID Card</b></TableCell>
                            <TableCell><b>User Type</b></TableCell>
                            <TableCell><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow><TableCell colSpan={8} align="center"><CircularProgress /></TableCell></TableRow>
                        ) : error ? (
                            <TableRow><TableCell colSpan={8} align="center"><Typography color="error">Error loading users</Typography></TableCell></TableRow>
                        ) : filteredData.length > 0 ? (
                            filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, index) => (
                                <TableRow key={index} hover selected={isSelected(row.id)}
                                    sx={{ backgroundColor: index % 2 === 0 ? "#F8F9E9" : "#ffffff" }}>
                                    <TableCell padding="checkbox">
                                        <Checkbox
                                            checked={isSelected(row.id)}
                                            onChange={(e) => handleCheckboxClick(e, row.id)}
                                            sx={{ color: "#57511F", '&.Mui-checked': { color: "#57511F" } }}
                                        />
                                    </TableCell>
                                    <TableCell>{row.First_Name || row.Last_Name ? `${row.First_Name || ""} ${row.Last_Name || ""}`.trim() : "N/A"}</TableCell>
                                    <TableCell>{row.Mobile_Number || "N/A"}</TableCell>
                                    <TableCell>{row.id || "N/A"}</TableCell>
                                    <TableCell>{row.Payment_Status || "Unpaid"}</TableCell>
                                    <TableCell>{row.ID_Card || "Not Generated"}</TableCell>
                                    <TableCell>{row.Role || "N/A"}</TableCell>
                                    <TableCell>
                                        <IconButton onClick={(e) => handleMenuOpen(e, row)}>
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu
                                            anchorEl={menuAnchorEl}
                                            open={Boolean(menuAnchorEl)}
                                            onClose={handleMenuClose}
                                            PaperProps={{ sx: { boxShadow: '0px 2px 4px rgba(0,0,0,0.1)', borderRadius: 1.5 } }}
                                        >
                                            <MenuItem onClick={() => handleEdit(menuUser)}><EditIcon sx={{ color: "blue", mr: 1 }} /> Edit</MenuItem>
                                            {menuUser?.Payment_Status !== "Paid" && (
                                                <MenuItem onClick={() => handlePayment(menuUser)}><PaymentIcon sx={{ color: "green", mr: 1 }} /> Pay</MenuItem>
                                            )}
                                            {storedUserType === "superuser" && (
                                                <MenuItem onClick={() => handleDelete(menuUser)}><DeleteIcon sx={{ color: "red", mr: 1 }} /> Delete</MenuItem>
                                            )}
                                        </Menu>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow><TableCell colSpan={8} align="center">No records found.</TableCell></TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Repeated Pagination Controls */}
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={filteredData.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />

            {/* Dialogs */}
            <EditUserDialog isOpen={openEditModal} onClose={() => setOpenEditModal(false)} user={selectedUser} />
            <DeleteConfirmationDialog open={openDialog && dialogMode === "delete"} onClose={() => setOpenDialog(false)} user={selectedUserForDelete} />
            <PaymentConfirmationDialog open={openDialog && dialogMode === "pay"} onClose={() => setOpenDialog(false)} user={selectedUserForPayment} />

            {/* Snackbar */}
            <Snackbar open={snackbarOpen} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: "top", horizontal: "right" }} />
        </div>
    );
};

export default DataTable;
